import { addDays, format, parseISO, startOfWeek } from "date-fns";
import {
    CUSTOMER_SUPPORT_ROLE_IDS,
    FUNCTION_OPTIONS,
    TaskDateModeKey,
    TaskStatusFilterKey,
    TeamFunctionFilterKey,
    TeamFunctionKey,
} from "./teamTasksConfig";
import { DateRange } from "../../../dashboard-framework/types";

// Rollups 100% client-side a partir de fetches raw, com dedup por task id em todos os totais
// (multi-atribuídas contam 1× por pessoa mas 1× no total — Q1 do spec.md).

export type TaskAssigneeRef = { directus_users_id: string | null } | null;

export type RawTask = {
    id: number;
    due_date?: string | null;
    date_created?: string | null;
    date_updated?: string | null;
    completed_at?: string | null;
    done_date?: string | null;
    assigned_users?: Array<TaskAssigneeRef> | null;
};

export type PersonInfo = {
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
    status?: string | null;
    role?: string | { id?: string } | null;
    technician_id?: Array<{ specializations?: Array<string> | null } | null> | null;
};

export type TeamTasksInputs = {
    openTasks: Array<RawTask>;
    doneTasks: Array<RawTask>;
    peopleById: Record<string, PersonInfo>;
};

export const UNASSIGNED_PERSON_KEY = "__sem_responsavel__";

// Pessoas = só utilizadores ATIVOS e identificados (decisão do stakeholder). Inativos (ex-staff)
// e users sem nome/email/role (automações antigas) agregam-se numa única linha — as tarefas
// abertas deles não desaparecem em silêncio. Ficam fora das funções; contam nos totais globais.
export const NON_ACTIVE_PERSON_KEY = "__inativos_sem_identificacao__";

export const UNKNOWN_PERSON_NAME = "Utilizador desconhecido";

const isIdentitylessPerson = (person: PersonInfo | undefined) => {
    const isIdentityless = Boolean(person) && !person?.first_name && !person?.last_name && !person?.email;
    return isIdentityless;
};

// Status null/ilegível conta como ativo (benefício da dúvida — se o viewer não ler o campo,
// a tabela não pode colapsar inteira para a linha agregada).
const isNonActivePerson = (person: PersonInfo | undefined) => {
    if (!person) return false;
    const isInactive = Boolean(person.status) && person.status !== "active";
    const isNonActive = isInactive || isIdentitylessPerson(person);
    return isNonActive;
};

const FUNCTION_LABEL_BY_KEY: Record<TeamFunctionKey, string> = FUNCTION_OPTIONS.reduce(
    (labels, option) => ({ ...labels, [option.key]: option.label }),
    {} as Record<TeamFunctionKey, string>
);

export const assigneeIdsOfTask = (task: RawTask) => {
    const assigneeIds = (task.assigned_users || [])
        .map((assigneeRef) => assigneeRef?.directus_users_id)
        .filter((assigneeId): assigneeId is string => Boolean(assigneeId));
    const uniqueAssigneeIds = Array.from(new Set(assigneeIds));
    return uniqueAssigneeIds;
};

// Réplica do agrupamento dos schedules (spec.md): especializações para NFB/Psicologia,
// role para apoio ao cliente, "outros" só quando nada bate (disjunto do apoio ao cliente).
export const functionsOfPerson = (person: PersonInfo) => {
    const specializations = person.technician_id?.[0]?.specializations || [];
    const roleId = typeof person.role === "string" ? person.role : person.role?.id || null;

    const personFunctions: Array<TeamFunctionKey> = [];
    if (specializations.includes("neurofeedback")) personFunctions.push("neurofeedback");
    if (specializations.includes("psicologia")) personFunctions.push("psicologia");
    if (roleId && CUSTOMER_SUPPORT_ROLE_IDS.includes(roleId)) personFunctions.push("apoio_cliente");
    if (personFunctions.length === 0) personFunctions.push("outros");
    return personFunctions;
};

export const taskDateForMode = (task: RawTask, dateMode: TaskDateModeKey) => {
    if (dateMode === "deadline") return task.due_date ?? null;
    if (dateMode === "conclusao") return task.done_date ?? null;
    const createdDate = task.date_created ?? null;
    return createdDate;
};

type PersonBucket = {
    openTaskIds: Set<number>;
    doneTaskIds: Set<number>;
};

const emptyBucket = (): PersonBucket => {
    const bucket = { openTaskIds: new Set<number>(), doneTaskIds: new Set<number>() };
    return bucket;
};

const personBucketsOf = (inputs: TeamTasksInputs) => {
    const bucketsByPersonKey = new Map<string, PersonBucket>();

    const bucketKeyOfAssignee = (assigneeId: string) => {
        const bucketKey = isNonActivePerson(inputs.peopleById[assigneeId]) ? NON_ACTIVE_PERSON_KEY : assigneeId;
        return bucketKey;
    };

    const addTaskTo = (task: RawTask, bucketKind: "openTaskIds" | "doneTaskIds") => {
        const assigneeIds = assigneeIdsOfTask(task);
        const mappedKeys = Array.from(new Set(assigneeIds.map(bucketKeyOfAssignee)));
        // O grupo de inativos só fica com a tarefa quando NENHUM responsável está ativo —
        // com um dono ativo a tarefa não está perdida (e os 3 grupos particionam o total).
        const ownerKeys = mappedKeys.filter((personKey) => personKey !== NON_ACTIVE_PERSON_KEY);
        const personKeys = assigneeIds.length === 0 ? [UNASSIGNED_PERSON_KEY] : ownerKeys.length > 0 ? ownerKeys : [NON_ACTIVE_PERSON_KEY];
        personKeys.forEach((personKey) => {
            const bucket = bucketsByPersonKey.get(personKey) || emptyBucket();
            bucket[bucketKind].add(task.id);
            bucketsByPersonKey.set(personKey, bucket);
        });
    };

    inputs.openTasks.forEach((task) => addTaskTo(task, "openTaskIds"));
    inputs.doneTasks.forEach((task) => addTaskTo(task, "doneTaskIds"));
    return bucketsByPersonKey;
};

const personMatchesFunction = (inputs: TeamTasksInputs, personKey: string, functionFilter: TeamFunctionFilterKey) => {
    if (functionFilter === "todas") return true;
    if (personKey === UNASSIGNED_PERSON_KEY || personKey === NON_ACTIVE_PERSON_KEY) return false;
    const person = inputs.peopleById[personKey];
    if (!person || isNonActivePerson(person)) return false;
    const matches = functionsOfPerson(person).includes(functionFilter);
    return matches;
};

const taskMatchesFunction = (inputs: TeamTasksInputs, task: RawTask, functionFilter: TeamFunctionFilterKey) => {
    if (functionFilter === "todas") return true;
    const matches = assigneeIdsOfTask(task).some((assigneeId) => personMatchesFunction(inputs, assigneeId, functionFilter));
    return matches;
};

const displayNameOf = (person: PersonInfo | undefined) => {
    if (!person) return UNKNOWN_PERSON_NAME;
    const composedName = [person.first_name, person.last_name].filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
    const displayName = composedName || person.email || UNKNOWN_PERSON_NAME;
    return displayName;
};

export type TeamTasksTableRow = {
    personKey: string;
    displayName: string;
    functionLabels: Array<string>;
    isUnassigned: boolean;
    isNonActiveGroup: boolean;
    openCount: number;
    doneCount: number;
    openTaskIds: Array<number>;
    doneTaskIds: Array<number>;
    isRed: boolean;
};

const rowPassesStatusFilter = (row: TeamTasksTableRow, statusFilter: TaskStatusFilterKey) => {
    if (statusFilter === "abertas") return row.openCount > 0;
    if (statusFilter === "feitas") return row.doneCount > 0;
    const passes = row.openCount > 0 || row.doneCount > 0;
    return passes;
};

export const teamTasksTableRows = (
    inputs: TeamTasksInputs,
    statusFilter: TaskStatusFilterKey,
    functionFilter: TeamFunctionFilterKey,
    redOpenThreshold: number
) => {
    const bucketsByPersonKey = personBucketsOf(inputs);

    const rows: Array<TeamTasksTableRow> = [];
    let unassignedRow: TeamTasksTableRow | null = null;
    let nonActiveGroupRow: TeamTasksTableRow | null = null;

    bucketsByPersonKey.forEach((bucket, personKey) => {
        const isUnassigned = personKey === UNASSIGNED_PERSON_KEY;
        const isNonActiveGroup = personKey === NON_ACTIVE_PERSON_KEY;
        const isAggregatedRow = isUnassigned || isNonActiveGroup;
        if (!isAggregatedRow && !personMatchesFunction(inputs, personKey, functionFilter)) return;
        if (isAggregatedRow && functionFilter !== "todas") return;

        const person = inputs.peopleById[personKey];
        const openCount = bucket.openTaskIds.size;
        const aggregatedName = isUnassigned ? "Sem responsável" : "Inativos / sem identificação";
        const row: TeamTasksTableRow = {
            personKey,
            displayName: isAggregatedRow ? aggregatedName : displayNameOf(person),
            functionLabels: isAggregatedRow || !person ? [] : functionsOfPerson(person).map((key) => FUNCTION_LABEL_BY_KEY[key]),
            isUnassigned,
            isNonActiveGroup,
            openCount,
            doneCount: bucket.doneTaskIds.size,
            openTaskIds: Array.from(bucket.openTaskIds),
            doneTaskIds: Array.from(bucket.doneTaskIds),
            isRed: !isAggregatedRow && openCount > redOpenThreshold,
        };
        if (!rowPassesStatusFilter(row, statusFilter)) return;

        if (isUnassigned) {
            unassignedRow = row;
            return;
        }
        if (isNonActiveGroup) {
            nonActiveGroupRow = row;
            return;
        }
        rows.push(row);
    });

    const sortValueOf = (row: TeamTasksTableRow) => {
        const sortValue = statusFilter === "feitas" ? row.doneCount : row.openCount;
        return sortValue;
    };
    rows.sort((firstRow, secondRow) => {
        const countDifference = sortValueOf(secondRow) - sortValueOf(firstRow);
        if (countDifference !== 0) return countDifference;
        const nameComparison = firstRow.displayName.localeCompare(secondRow.displayName, "pt");
        return nameComparison;
    });

    const aggregatedRows: Array<TeamTasksTableRow | null> = [nonActiveGroupRow, unassignedRow];
    const presentAggregatedRows = aggregatedRows.filter((row): row is TeamTasksTableRow => row !== null);
    const allRows = [...rows, ...presentAggregatedRows];
    return allRows;
};

export type SpecialGroupTotals = {
    openCount: number;
    doneCount: number;
    openTaskIds: Array<number>;
    doneTaskIds: Array<number>;
};

// Totais dos dois grupos que não são pessoas ativas — com o "dono ativo ganha" no bucket de
// inativos, (pessoas ativas distintas) + nonActive + unassigned == total global, exatamente.
export const teamTasksSpecialGroupTotals = (inputs: TeamTasksInputs) => {
    const bucketsByPersonKey = personBucketsOf(inputs);
    const totalsOf = (personKey: string): SpecialGroupTotals => {
        const bucket = bucketsByPersonKey.get(personKey);
        const openTaskIds = Array.from(bucket?.openTaskIds || []);
        const doneTaskIds = Array.from(bucket?.doneTaskIds || []);
        const totals = { openCount: openTaskIds.length, doneCount: doneTaskIds.length, openTaskIds, doneTaskIds };
        return totals;
    };
    const specialGroups = { nonActive: totalsOf(NON_ACTIVE_PERSON_KEY), unassigned: totalsOf(UNASSIGNED_PERSON_KEY) };
    return specialGroups;
};

export type TeamTasksGlobalCards = {
    openTotal: number;
    doneTotal: number;
    redCount: number;
    openTaskIds: Array<number>;
    doneTaskIds: Array<number>;
};

export const teamTasksGlobalCards = (
    inputs: TeamTasksInputs,
    functionFilter: TeamFunctionFilterKey,
    redOpenThreshold: number
): TeamTasksGlobalCards => {
    const distinctMatchingTaskIds = (tasks: Array<RawTask>) => {
        const taskIds = new Set<number>();
        tasks.forEach((task) => {
            if (taskMatchesFunction(inputs, task, functionFilter)) taskIds.add(task.id);
        });
        return taskIds;
    };

    const bucketsByPersonKey = personBucketsOf(inputs);
    let redCount = 0;
    bucketsByPersonKey.forEach((bucket, personKey) => {
        if (personKey === UNASSIGNED_PERSON_KEY || personKey === NON_ACTIVE_PERSON_KEY) return;
        if (!personMatchesFunction(inputs, personKey, functionFilter)) return;
        if (bucket.openTaskIds.size > redOpenThreshold) redCount += 1;
    });

    const openTaskIds = distinctMatchingTaskIds(inputs.openTasks);
    const doneTaskIds = distinctMatchingTaskIds(inputs.doneTasks);
    const cards = {
        openTotal: openTaskIds.size,
        doneTotal: doneTaskIds.size,
        redCount,
        openTaskIds: Array.from(openTaskIds),
        doneTaskIds: Array.from(doneTaskIds),
    };
    return cards;
};

export type TeamTasksFunctionCard = {
    functionKey: TeamFunctionKey;
    functionLabel: string;
    openCount: number;
    doneCount: number;
    openTaskIds: Array<number>;
    doneTaskIds: Array<number>;
};

export const teamTasksFunctionCards = (inputs: TeamTasksInputs, functionFilter: TeamFunctionFilterKey) => {
    const visibleOptions = FUNCTION_OPTIONS.filter((option) => functionFilter === "todas" || option.key === functionFilter);

    const cards = visibleOptions.map((option) => {
        const distinctTaskIdsOf = (tasks: Array<RawTask>) => {
            const taskIds = new Set<number>();
            tasks.forEach((task) => {
                const belongsToFunction = assigneeIdsOfTask(task).some((assigneeId) =>
                    personMatchesFunction(inputs, assigneeId, option.key)
                );
                if (belongsToFunction) taskIds.add(task.id);
            });
            return taskIds;
        };

        const openTaskIds = distinctTaskIdsOf(inputs.openTasks);
        const doneTaskIds = distinctTaskIdsOf(inputs.doneTasks);
        const card: TeamTasksFunctionCard = {
            functionKey: option.key,
            functionLabel: option.label,
            openCount: openTaskIds.size,
            doneCount: doneTaskIds.size,
            openTaskIds: Array.from(openTaskIds),
            doneTaskIds: Array.from(doneTaskIds),
        };
        return card;
    });
    return cards;
};

export type TeamTasksWeeklyPoint = {
    weekStart: string;
    weekLabel: string;
    doneCount: number;
};

// Semanas ISO (segunda a domingo) sobre a parte de data (UTC) dos timestamps — a mesma base
// de comparação do recorte do servidor, para a soma dos pontos bater com o cartão "Feitas".
export const teamTasksWeeklyPoints = (
    inputs: TeamTasksInputs,
    functionFilter: TeamFunctionFilterKey,
    personKey: string,
    dateMode: TaskDateModeKey,
    range: DateRange
) => {
    const taskMatchesPerson = (task: RawTask) => {
        if (personKey === "all") {
            const matches = taskMatchesFunction(inputs, task, functionFilter);
            return matches;
        }
        const matches = assigneeIdsOfTask(task).includes(personKey);
        return matches;
    };

    const weekKeyOf = (dayString: string) => {
        const weekStart = startOfWeek(parseISO(dayString), { weekStartsOn: 1 });
        const weekKey = format(weekStart, "yyyy-MM-dd");
        return weekKey;
    };

    const taskIdsByWeekKey = new Map<string, Set<number>>();
    inputs.doneTasks.forEach((task) => {
        if (!taskMatchesPerson(task)) return;
        const taskDate = taskDateForMode(task, dateMode);
        if (!taskDate) return;
        const dayString = taskDate.slice(0, 10);
        if (dayString < range.gte || dayString >= range.lt) return;
        const weekKey = weekKeyOf(dayString);
        const weekTaskIds = taskIdsByWeekKey.get(weekKey) || new Set<number>();
        weekTaskIds.add(task.id);
        taskIdsByWeekKey.set(weekKey, weekTaskIds);
    });

    const points: Array<TeamTasksWeeklyPoint> = [];
    let weekStartDate = startOfWeek(parseISO(range.gte), { weekStartsOn: 1 });
    while (format(weekStartDate, "yyyy-MM-dd") < range.lt) {
        const weekKey = format(weekStartDate, "yyyy-MM-dd");
        const point = {
            weekStart: weekKey,
            weekLabel: `Sem. ${format(weekStartDate, "dd/MM")}`,
            doneCount: taskIdsByWeekKey.get(weekKey)?.size || 0,
        };
        points.push(point);
        weekStartDate = addDays(weekStartDate, 7);
    }
    return points;
};

const HTML_ENTITY_MAP: Record<string, string> = {
    nbsp: " ",
    amp: "&",
    lt: "<",
    gt: ">",
    quot: '"',
    apos: "'",
    agrave: "à",
    aacute: "á",
    acirc: "â",
    atilde: "ã",
    egrave: "è",
    eacute: "é",
    ecirc: "ê",
    igrave: "ì",
    iacute: "í",
    ograve: "ò",
    oacute: "ó",
    ocirc: "ô",
    otilde: "õ",
    ugrave: "ù",
    uacute: "ú",
    ccedil: "ç",
    Agrave: "À",
    Aacute: "Á",
    Acirc: "Â",
    Atilde: "Ã",
    Eacute: "É",
    Ecirc: "Ê",
    Iacute: "Í",
    Oacute: "Ó",
    Otilde: "Õ",
    Uacute: "Ú",
    Ccedil: "Ç",
};

export const stripTaskHtml = (htmlContent: string | null | undefined) => {
    if (!htmlContent) return "(sem descrição)";
    const withoutTags = htmlContent.replace(/<[^>]*>/g, " ");
    const withNamedEntities = withoutTags.replace(/&([a-zA-Z]+);/g, (fullMatch, entityName) => {
        const decoded = HTML_ENTITY_MAP[entityName];
        return decoded ?? fullMatch;
    });
    const withNumericEntities = withNamedEntities.replace(/&#(\d+);/g, (_fullMatch, codePoint) => String.fromCharCode(Number(codePoint)));
    const normalized = withNumericEntities.replace(/\s+/g, " ").trim();
    const stripped = normalized || "(sem descrição)";
    return stripped;
};
